"use client";

import React, { useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Copy, Trash2, Plus, GripVertical } from "lucide-react";
import { cn } from "@/utils/utils";
import { Button } from "@/components/ui/button";
import {
  FilterGroup,
  FilterGroupLogic,
  AudienceFilter,
} from "../../types/audienceTypes";
import FilterRow from "../FilterRow/FilterRow";

// ============================================================================
// Component Props
// ============================================================================

interface FilterGroupCardProps {
  group: FilterGroup;
  groupNumber: number;
  onUpdate: (group: FilterGroup) => void;
  onRemove: () => void;
  onClone: () => void;
  onAddFilter: () => void;
}

// ============================================================================
// Component
// ============================================================================

export function FilterGroupCard({
  group,
  groupNumber,
  onUpdate,
  onRemove,
  onClone,
  onAddFilter,
}: FilterGroupCardProps) {
  // Toggle internal logic
  const handleToggleLogic = useCallback(() => {
    onUpdate({
      ...group,
      logic: group.logic === "AND" ? "OR" : "AND",
    });
  }, [group, onUpdate]);

  // Update a filter
  const handleUpdateFilter = useCallback(
    (filterId: string, updates: Partial<AudienceFilter>) => {
      onUpdate({
        ...group,
        filters: group.filters.map((f) =>
          f.id === filterId ? { ...f, ...updates } : f
        ),
      });
    },
    [group, onUpdate]
  );

  // Remove a filter
  const handleRemoveFilter = useCallback(
    (filterId: string) => {
      const newFilters = group.filters.filter((f) => f.id !== filterId);
      if (newFilters.length === 0) {
        onRemove();
      } else {
        onUpdate({
          ...group,
          filters: newFilters,
        });
      }
    },
    [group, onUpdate, onRemove]
  );

  return (
    <div className="ab-card overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-3 border-b border-[var(--ab-border)] bg-[var(--ab-bg-tertiary)]/30">
        <div className="flex items-center gap-3">
          <div className="cursor-grab text-[var(--ab-text-muted)] hover:text-[var(--ab-text-secondary)]">
            <GripVertical className="w-4 h-4" />
          </div>
          <span className="ab-mono text-xs font-medium text-[var(--ab-text-muted)] uppercase tracking-wider">
            Filter Group {groupNumber}
          </span>
          <span className="text-xs text-[var(--ab-text-muted)]">
            {group.filters.length} {group.filters.length === 1 ? "filter" : "filters"}
          </span>
        </div>

        <div className="flex items-center gap-1">
          <button
            className="p-2 rounded-lg text-[var(--ab-text-muted)] hover:text-[var(--ab-text-secondary)] hover:bg-[var(--ab-bg-primary)] transition-all"
            onClick={onClone}
            title="Clone group"
          >
            <Copy className="w-4 h-4" />
          </button>
          <button
            className="p-2 rounded-lg text-[var(--ab-text-muted)] hover:text-red-400 hover:bg-red-500/10 transition-all"
            onClick={onRemove}
            title="Delete group"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="p-5 space-y-3">
        {/* @ts-ignore - framer-motion type incompatibility with React 18 */}
        <AnimatePresence mode="popLayout">
          {group.filters.map((filter, index) => (
            <React.Fragment key={filter.id}>
              {/* Internal Logic Connector */}
              {index > 0 && (
                <motion.div
                  animate={{ opacity: 1 }}
                  className="flex items-center gap-3 py-1"
                  exit={{ opacity: 0 }}
                  initial={{ opacity: 0 }}
                >
                  <div className="flex-1 h-px bg-[var(--ab-border)]" />
                  <button
                    className={cn(
                      "ab-logic-pill text-[10px]",
                      group.logic === "AND" ? "ab-logic-and" : "ab-logic-or"
                    )}
                    onClick={handleToggleLogic}
                  >
                    {group.logic}
                  </button>
                  <div className="flex-1 h-px bg-[var(--ab-border)]" />
                </motion.div>
              )}

              {/* Filter Row */}
              <motion.div
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                initial={{ opacity: 0, x: 20 }}
                layout
                transition={{ type: "spring", stiffness: 300, damping: 30 }}
              >
                <FilterRow
                  filter={filter}
                  onRemove={() => handleRemoveFilter(filter.id)}
                  onUpdate={(updates) => handleUpdateFilter(filter.id, updates)}
                />
              </motion.div>
            </React.Fragment>
          ))}
        </AnimatePresence>

        {/* Add Filter Button */}
        <motion.div layout>
          <Button
            className="w-full mt-2 text-[var(--ab-text-muted)] hover:text-[var(--ab-accent)] border-[var(--ab-border)] border-dashed hover:border-[var(--ab-accent)] hover:bg-[var(--ab-accent)]/5 bg-transparent"
            onClick={onAddFilter}
            variant="outline"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Filter
          </Button>
        </motion.div>
      </div>
    </div>
  );
}

export default FilterGroupCard;
